<template>
  <div class="dashboard">
    <!-- KPI 卡片 -->
    <el-row :gutter="16" class="row-gap">
      <el-col :xs="12" :sm="12" :md="6" v-for="kpi in kpiData" :key="kpi.label">
        <div class="kpi-card" :style="{ borderLeftColor: kpi.color }">
          <div class="kpi-icon" :style="{ background: kpi.bg, color: kpi.color }">
            <i :class="kpi.icon"></i>
          </div>
          <div class="kpi-info">
            <div class="kpi-value">{{ fmt(kpi.value) }}</div>
            <div class="kpi-label">{{ kpi.label }}</div>
            <div class="kpi-sub" v-if="kpi.sub" :style="{ color: kpi.color }">{{ kpi.sub }}</div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 白名单数据概览 + 申领和核销统计 -->
    <el-row :gutter="16" class="row-gap row-equal">
      <el-col :xs="24" :md="14">
        <el-card shadow="hover" class="section-card">
          <div slot="header" class="card-header">
            <span><i class="el-icon-s-data"></i> 白名单数据概览</span>
            <el-tag size="mini" type="info">转化漏斗</el-tag>
          </div>
          <div class="funnel-wrap">
            <ul class="funnel-list">
              <li v-for="(f, i) in funnelData" :key="i" class="funnel-item">
                <span class="funnel-dot" :style="{ background: f.color }"></span>
                <div class="funnel-text">
                  <div class="funnel-label">{{ f.label }}</div>
                  <div class="funnel-value" :style="{ color: f.color }">{{ fmt(f.value) }}</div>
                </div>
                <span class="funnel-rate" v-if="f.rate">{{ f.rate }}%</span>
              </li>
            </ul>
            <div ref="funnelChart" class="funnel-chart"></div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :md="10">
        <el-card shadow="hover" class="section-card">
          <div slot="header" class="card-header">
            <span><i class="el-icon-s-claim"></i> 申领和核销统计</span>
          </div>

          <div class="stat-block">
            <div class="stat-block-title">今日数据</div>
            <div class="mini-grid">
              <div class="mini-item">
                <div class="mini-value blue">{{ fmt(overview.todayClaims) }}</div>
                <div class="mini-label">今日申领数</div>
              </div>
              <div class="mini-item">
                <div class="mini-value green">{{ fmt(overview.todayVerified) }}</div>
                <div class="mini-label">今日核销数</div>
              </div>
            </div>
          </div>

          <div class="stat-block">
            <div class="stat-block-title">累计数据</div>
            <div class="mini-grid">
              <div class="mini-item">
                <div class="mini-value">{{ fmt(totalStats.claims) }}</div>
                <div class="mini-label">总申领数</div>
              </div>
              <div class="mini-item">
                <div class="mini-value">{{ fmt(totalStats.verified) }}</div>
                <div class="mini-label">总核销数</div>
              </div>
              <div class="mini-item">
                <div class="mini-value orange">{{ fmt(totalStats.weekNew) }}</div>
                <div class="mini-label">本周新增</div>
              </div>
              <div class="mini-item">
                <div class="mini-value purple">{{ verifyRate }}%</div>
                <div class="mini-label">核销率</div>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- ✅ 近6个月申领与核销趋势 -->
    <el-card shadow="hover" class="section-card row-gap">
      <div slot="header" class="card-header">
        <span><i class="el-icon-date"></i> 近6个月申领与核销趋势</span>
        <el-tag size="mini" type="success">月度趋势</el-tag>
      </div>
      <div ref="monthlyTrendChart" class="chart-container"></div>
    </el-card>

    <!-- 各类型领取人 -->
    <el-row :gutter="16" class="row-gap">
      <el-col :xs="24" :md="12">
        <el-card shadow="hover" class="section-card">
          <div slot="header" class="card-header">
            <span><i class="el-icon-s-order"></i> 各类型领取人·总领取数</span>
            <span class="card-total">{{ fmt(recipientSum('claim')) }}</span>
          </div>
          <div ref="claimTypeChart" class="chart-container"></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="12">
        <el-card shadow="hover" class="section-card">
          <div slot="header" class="card-header">
            <span><i class="el-icon-circle-check"></i> 各类型领取人·总核销数</span>
            <span class="card-total">{{ fmt(recipientSum('verify')) }}</span>
          </div>
          <div ref="verifyTypeChart" class="chart-container"></div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 用户统计 -->
    <el-row :gutter="16" class="row-gap row-equal">
      <el-col :xs="24" :md="8">
        <div class="user-total-card">
          <div class="user-total-label">全平台用户总数</div>
          <div class="user-total-value">{{ fmt(userStats.total) }}</div>
          <div class="user-total-foot">
            <span class="growth">本月增长 {{ userStats.monthGrowth }}%</span>
            <span class="new-add">新增 {{ fmt(userStats.monthNew) }} 人</span>
          </div>
        </div>
      </el-col>
      <el-col :xs="24" :md="8">
        <el-card shadow="hover" class="section-card user-card">
          <div class="user-title">用户列表数量</div>
          <div class="user-value">{{ fmt(userStats.listCount) }}</div>
          <el-progress :percentage="userStats.listRate" :stroke-width="8" color="#409EFF" :show-text="false"></el-progress>
          <div class="user-foot">占比 {{ userStats.listRate }}%</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="8">
        <el-card shadow="hover" class="section-card user-card">
          <div class="user-title">白名单激活人数</div>
          <div class="user-value">{{ fmt(userStats.whitelistCount) }}</div>
          <el-progress :percentage="userStats.whitelistRate" :stroke-width="8" color="#67C23A" :show-text="false"></el-progress>
          <div class="user-foot">占比 {{ userStats.whitelistRate }}%</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 商家数据统计 -->
    <el-card shadow="hover" class="section-card row-gap">
      <div slot="header" class="card-header">
        <span><i class="el-icon-s-shop"></i> 商家数据统计</span>
        <el-tag size="mini" type="info">入驻·发布·核销</el-tag>
      </div>
      <el-row :gutter="16">
        <el-col :xs="24" :md="8" v-for="m in merchantCards" :key="m.label">
          <div class="merchant-card">
            <div class="merchant-icon" :style="{ background: m.bg, color: m.color }">
              <i :class="m.icon"></i>
            </div>
            <div class="merchant-info">
              <div class="merchant-label">{{ m.label }}</div>
              <div class="merchant-value">{{ fmt(m.value) }}</div>
              <div class="merchant-sub">{{ m.sub }}</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- 快捷操作 -->
    <el-card shadow="hover" class="section-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-menu"></i> 快捷操作</span>
      </div>
      <div class="quick-actions">
        <div v-for="q in quickActions" :key="q.path" class="quick-item" @click="$router.push(q.path)">
          <i :class="q.icon" :style="{ color: q.color }"></i>
          <span>{{ q.label }}</span>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getOverview } from '@/api/dashboard'

const DEFAULT_OVERVIEW = {
  totalUsers: 26439,
  todayClaims: 186,
  todayVerified: 142,
  pendingMerchants: 0,
  whitelist: { imported: 12847, activated: 8392, claimed: 5618 },
  total: { claims: 9854, verified: 7241, weekNew: 2 },
  user: {
    total: 26439,
    monthGrowth: 8.2,
    monthNew: 2006,
    listCount: 18047,
    listRate: 68.3,
    whitelistCount: 8392,
    whitelistRate: 31.7
  },
  merchant: { total: 286, monthNew: 18, published: 1572, verified: 7241 },
  recipients: [
    { name: '普通用户', claim: 3256, verify: 2418 },
    { name: '社区用户', claim: 2847, verify: 2105 },
    { name: '志愿者', claim: 1923, verify: 1486 },
    { name: '合作机构', claim: 1205, verify: 892 },
    { name: '特殊群体', claim: 623, verify: 340 }
  ],
  // ✅ 近6个月，与 recipients 同级
  monthlyStats: [
    { month: '2026-04', claim: 1250, verify: 980 },
    { month: '2026-05', claim: 1420, verify: 1100 },
    { month: '2026-06', claim: 1380, verify: 1050 },
    { month: '2026-07', claim: 1650, verify: 1280 },
    { month: '2026-08', claim: 1820, verify: 1450 },
    { month: '2026-09', claim: 2100, verify: 1680 }
  ]
}

function mergeOverview(defaults, data) {
  const out = Object.assign({}, defaults, data || {})
  ;['whitelist', 'total', 'user', 'merchant'].forEach(key => {
    out[key] = Object.assign({}, defaults[key], (data && data[key]) || {})
  })
  if (!Array.isArray(out.recipients) || !out.recipients.length) {
    out.recipients = defaults.recipients
  }
  if (!Array.isArray(out.monthlyStats) || !out.monthlyStats.length) {
    out.monthlyStats = defaults.monthlyStats
  }
  return out
}

export default {
  name: 'Dashboard',
  data() {
    return {
      overview: mergeOverview(DEFAULT_OVERVIEW, null),
      kpiData: [
        { label: '累计用户', value: 0, sub: '', icon: 'el-icon-user-solid', color: '#409EFF', bg: '#ecf5ff' },
        { label: '今日申领', value: 0, sub: '', icon: 'el-icon-s-order', color: '#67C23A', bg: '#f0f9eb' },
        { label: '今日核销', value: 0, sub: '', icon: 'el-icon-circle-check', color: '#E6A23C', bg: '#fdf6ec' },
        { label: '待审核商户', value: 0, sub: '', icon: 'el-icon-s-shop', color: '#F56C6C', bg: '#fef0f0' }
      ],
      trendData: { dates: [], claim: [], verify: [] },
      todos: [],
      quickActions: [
        { label: '商户审核', icon: 'el-icon-s-shop', color: '#E6A23C', path: '/merchant/list' },
        { label: '实名认证', icon: 'el-icon-document-checked', color: '#409EFF', path: '/user/verification' },
        { label: '用户管理', icon: 'el-icon-user', color: '#67C23A', path: '/user/list' },
        { label: '商品管理', icon: 'el-icon-s-goods', color: '#F56C6C', path: '/product/list' },
        { label: '白名单', icon: 'el-icon-s-check', color: '#909399', path: '/whitelist/list' },
        { label: '消息推送', icon: 'el-icon-message', color: '#9B59B6', path: '/notification/list' }
      ]
    }
  },
  computed: {
    totalStats() {
      return this.overview.total || {}
    },
    userStats() {
      return this.overview.user || {}
    },
    merchantStats() {
      return this.overview.merchant || {}
    },
    funnelData() {
      const w = this.overview.whitelist || {}
      return [
        { label: '累计导入白名单', value: w.imported || 0, color: '#409EFF' },
        { label: '社区激活', value: w.activated || 0, rate: this.pct(w.activated, w.imported), color: '#36CFC9' },
        { label: '激活后领取', value: w.claimed || 0, rate: this.pct(w.claimed, w.activated), color: '#FAAD14' }
      ]
    },
    verifyRate() {
      return this.pct(this.totalStats.verified, this.totalStats.claims)
    },
    merchantCards() {
      const m = this.merchantStats
      return [
        { label: '全平台商家数量', value: m.total || 0, sub: `本月新增 ${this.fmt(m.monthNew)} 家`, icon: 'el-icon-s-shop', color: '#409EFF', bg: '#ecf5ff' },
        { label: '商家发布总数', value: m.published || 0, sub: '商品/活动合计', icon: 'el-icon-menu', color: '#67C23A', bg: '#f0f9eb' },
        { label: '商家核销总数', value: m.verified || 0, sub: '累计核销订单', icon: 'el-icon-circle-check', color: '#E6A23C', bg: '#fdf6ec' }
      ]
    }
  },
  mounted() {
    this.loadData()
    this._resizeTimer = null
    this._onResize = () => {
      clearTimeout(this._resizeTimer)
      this._resizeTimer = setTimeout(() => this.renderCharts(), 200)
    }
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._resizeTimer)
    clearTimeout(this._resizeTimer)
  },
  methods: {
    async loadData() {
      try {
        const [overview] = await Promise.all([
          getOverview().catch(() => ({}))
        ])

        this.overview = mergeOverview(DEFAULT_OVERVIEW, overview)

        this.kpiData[0].value = this.overview.totalUsers || 0
        this.kpiData[0].sub = `本月新增 ${this.fmt(this.userStats.monthNew)} 人`
        this.kpiData[1].value = this.overview.todayClaims || 0
        this.kpiData[1].sub = `今日核销 ${this.fmt(this.overview.todayVerified)}`
        this.kpiData[2].value = this.overview.todayVerified || 0
        this.kpiData[2].sub = `核销率 ${this.verifyRate}%`
        this.kpiData[3].value = this.overview.pendingMerchants || 0
        this.kpiData[3].sub = this.overview.pendingMerchants ? '待处理' : '暂无待审核'

        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        console.error('Dashboard load error', e)
      }
    },
    fmt(n) {
      if (n === null || n === undefined || isNaN(n)) return '0'
      return Number(n).toLocaleString('en-US')
    },
    pct(a, b) {
      if (!b) return 0
      return Math.round((Number(a) / Number(b)) * 1000) / 10
    },
    recipientSum(field) {
      return (this.overview.recipients || []).reduce((s, r) => s + (Number(r[field]) || 0), 0)
    },
    renderCharts() {
      this.renderFunnel()
      this.renderMonthlyTrendChart()
      this.renderRecipientChart('claimTypeChart', 'claim', '#409EFF')
      this.renderRecipientChart('verifyTypeChart', 'verify', '#67C23A')
    },

    renderFunnel() {
      const el = this.$refs.funnelChart
      if (!el) return
      const w = el.clientWidth || 320
      const h = 240
      const gap = 8
      const levels = this.funnelData
      const maxV = levels[0].value || 1
      const maxW = Math.max(w - 24, 80)
      const minW = Math.min(70, maxW)
      const itemH = (h - gap * (levels.length - 1)) / levels.length
      const cx = w / 2
      const widthOf = v => minW + (maxW - minW) * ((v || 0) / maxV)

      let svg = `<svg width="${w}" height="${h}" xmlns="http://www.w3.org/2000/svg">`
      levels.forEach((lv, i) => {
        const topW = i === 0 ? maxW : widthOf(levels[i - 1].value)
        const botW = widthOf(lv.value)
        const y = i * (itemH + gap)
        const x1 = cx - topW / 2
        const x2 = cx + topW / 2
        const x3 = cx + botW / 2
        const x4 = cx - botW / 2
        svg += `<path d="M${x1},${y} L${x2},${y} L${x3},${y + itemH} L${x4},${y + itemH} Z" fill="${lv.color}" opacity="0.92"/>`
        svg += `<text x="${cx}" y="${y + itemH / 2 + 4}" text-anchor="middle" font-size="12" fill="#fff" font-weight="600">${this.fmt(lv.value)}</text>`
      })
      svg += `</svg>`
      el.innerHTML = svg
    },

    renderRecipientChart(refName, field, color) {
      const el = this.$refs[refName]
      if (!el) return
      const data = this.overview.recipients || []
      const w = el.clientWidth || 480
      const h = 280
      const padding = { top: 30, right: 16, bottom: 40, left: 50 }
      const innerW = w - padding.left - padding.right
      const innerH = h - padding.top - padding.bottom

      const values = data.map(d => Number(d[field]) || 0)
      const maxRaw = Math.max(...values, 1)
      const step = Math.pow(10, String(Math.round(maxRaw)).length - 1) || 1
      const max = Math.ceil((maxRaw * 1.15) / step) * step || 1

      const groupW = innerW / (data.length || 1)
      const barW = Math.min(groupW - 20, 42)

      let svg = `<svg width="${w}" height="${h}" xmlns="http://www.w3.org/2000/svg">`
      for (let i = 0; i <= 4; i++) {
        const y = padding.top + (innerH / 4) * i
        const val = Math.round(max - (max / 4) * i)
        svg += `<line x1="${padding.left}" y1="${y}" x2="${w - padding.right}" y2="${y}" stroke="#ebeef5" stroke-width="1"/>`
        svg += `<text x="${padding.left - 8}" y="${y + 4}" text-anchor="end" font-size="11" fill="#909399">${this.fmt(val)}</text>`
      }
      data.forEach((d, i) => {
        const v = values[i]
        const barH = (v / max) * innerH
        const cx = padding.left + groupW * i + groupW / 2
        const x = cx - barW / 2
        const y = padding.top + innerH - barH
        svg += `<rect x="${x}" y="${y}" width="${barW}" height="${barH}" rx="3" fill="${color}" opacity="0.88"/>`
        svg += `<text x="${cx}" y="${y - 6}" text-anchor="middle" font-size="11" fill="#606266">${this.fmt(v)}</text>`
        svg += `<text x="${cx}" y="${h - padding.bottom + 18}" text-anchor="middle" font-size="11" fill="#606266">${d.name}</text>`
      })
      svg += `</svg>`
      el.innerHTML = svg
    },

    // ✅ 近6个月趋势（数值显示在柱子顶部）
    renderMonthlyTrendChart() {
      const el = this.$refs.monthlyTrendChart
      if (!el) return

      const data = this.overview.monthlyStats || []
      if (!data.length) return

      const w = el.clientWidth || 720
      const h = 320
      const padding = { top: 40, right: 30, bottom: 50, left: 60 }
      const innerW = w - padding.left - padding.right
      const innerH = h - padding.top - padding.bottom

      const maxRaw = Math.max(...data.map(d => Math.max(d.claim, d.verify)), 1)
      const step = Math.pow(10, String(Math.round(maxRaw)).length - 1) || 1
      const max = Math.ceil((maxRaw * 1.2) / step) * step || 1

      const groupW = innerW / data.length
      const barW = Math.min((groupW - 24) / 2, 28)

      let svg = `<svg width="${w}" height="${h}" xmlns="http://www.w3.org/2000/svg">`

      // Y 轴
      for (let i = 0; i <= 5; i++) {
        const y = padding.top + (innerH / 5) * i
        const val = Math.round(max - (max / 5) * i)
        svg += `<line x1="${padding.left}" y1="${y}" x2="${w - padding.right}" y2="${y}" stroke="#ebeef5"/>`
        svg += `<text x="${padding.left - 10}" y="${y + 4}" text-anchor="end" font-size="11" fill="#909399">${this.fmt(val)}</text>`
      }

      // 柱子
      data.forEach((d, i) => {
        const cx = padding.left + groupW * i + groupW / 2

        const claimH = (d.claim / max) * innerH
        const claimX = cx - barW - 2
        const claimY = padding.top + innerH - claimH
        svg += `<rect x="${claimX}" y="${claimY}" width="${barW}" height="${claimH}" rx="3" fill="#409EFF" opacity="0.9"/>`
        svg += `<text x="${claimX + barW / 2}" y="${claimY - 6}" text-anchor="middle" font-size="11" fill="#409EFF" font-weight="600">${this.fmt(d.claim)}</text>`

        const verifyH = (d.verify / max) * innerH
        const verifyX = cx + 2
        const verifyY = padding.top + innerH - verifyH
        svg += `<rect x="${verifyX}" y="${verifyY}" width="${barW}" height="${verifyH}" rx="3" fill="#67C23A" opacity="0.9"/>`
        svg += `<text x="${verifyX + barW / 2}" y="${verifyY - 6}" text-anchor="middle" font-size="11" fill="#67C23A" font-weight="600">${this.fmt(d.verify)}</text>`

        svg += `<text x="${cx}" y="${h - padding.bottom + 20}" text-anchor="middle" font-size="12" fill="#606266">${d.month}</text>`
      })

      // 图例
      svg += `<rect x="${padding.left}" y="10" width="12" height="12" rx="2" fill="#409EFF"/>`
      svg += `<text x="${padding.left + 18}" y="20" font-size="12" fill="#606266">申领</text>`
      svg += `<rect x="${padding.left + 70}" y="10" width="12" height="12" rx="2" fill="#67C23A"/>`
      svg += `<text x="${padding.left + 88}" y="20" font-size="12" fill="#606266">核销</text>`

      svg += `</svg>`
      el.innerHTML = svg
    }
  }
}
</script>

<style scoped>
.dashboard { padding: 0; }
.row-gap { margin-bottom: 16px; }

.row-equal { display: flex; flex-wrap: wrap; }
.row-equal >>> .el-col { display: flex; }
.row-equal >>> .el-col > .el-card { flex: 1; }

.section-card { border-radius: 8px; }
.card-header {
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.card-header i { margin-right: 4px; }
.card-total { font-size: 12px; font-weight: 400; color: #909399; }

.kpi-card {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  padding: 18px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,.06);
  border-left: 4px solid #409EFF;
  transition: transform .2s;
}
.kpi-card:hover { transform: translateY(-2px); }
.kpi-icon {
  width: 48px; height: 48px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}
.kpi-icon i { font-size: 22px; }
.kpi-info { min-width: 0; }
.kpi-value { font-size: 24px; font-weight: bold; color: #303133; line-height: 1.2; }
.kpi-label { font-size: 13px; color: #909399; margin-top: 2px; }
.kpi-sub { font-size: 12px; margin-top: 4px; }

.funnel-wrap { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.funnel-list { list-style: none; padding: 0; margin: 0; flex: 1 1 200px; min-width: 180px; }
.funnel-item {
  display: flex; align-items: center;
  padding: 12px 0;
  border-bottom: 1px dashed #f0f2f5;
}
.funnel-item:last-child { border-bottom: none; }
.funnel-dot { width: 8px; height: 8px; border-radius: 50%; margin-right: 10px; flex-shrink: 0; }
.funnel-text { flex: 1; }
.funnel-label { font-size: 13px; color: #909399; }
.funnel-value { font-size: 18px; font-weight: bold; margin-top: 2px; }
.funnel-rate {
  font-size: 12px; color: #67C23A;
  background: #f0f9eb; border-radius: 10px;
  padding: 2px 8px;
}
.funnel-chart { flex: 1 1 240px; min-width: 220px; height: 240px; overflow: hidden; }

.stat-block + .stat-block { margin-top: 18px; padding-top: 14px; border-top: 1px solid #f0f2f5; }
.stat-block-title { font-size: 13px; color: #909399; margin-bottom: 10px; }
.mini-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
.mini-item { background: #f8f9fa; border-radius: 8px; padding: 12px; }
.mini-value { font-size: 20px; font-weight: bold; color: #303133; }
.mini-value.blue { color: #409EFF; }
.mini-value.green { color: #67C23A; }
.mini-value.orange { color: #E6A23C; }
.mini-value.purple { color: #9B59B6; }
.mini-label { font-size: 12px; color: #909399; margin-top: 2px; }

.chart-container { width: 100%; height: 320px; overflow: hidden; }

.user-total-card {
  background: linear-gradient(135deg,#2b6cff 0%,#1e3a8a 100%);
  border-radius: 8px;
  padding: 22px;
  color: #fff;
  height: 100%;
  box-shadow: 0 2px 12px rgba(30,58,138,.18);
}
.user-total-label { font-size: 13px; opacity: .85; }
.user-total-value { font-size: 30px; font-weight: bold; margin: 8px 0 12px; }
.user-total-foot { display: flex; justify-content: space-between; font-size: 12px; opacity: .9; }
.user-card { text-align: left; }
.user-title { font-size: 13px; color: #909399; }
.user-value { font-size: 26px; font-weight: bold; color: #303133; margin: 6px 0 10px; }
.user-foot { font-size: 12px; color: #909399; margin-top: 8px; }

.merchant-card {
  display: flex; align-items: center;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  transition: all .2s;
}
.merchant-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,.08); transform: translateY(-2px); }
.merchant-icon {
  width: 48px; height: 48px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}
.merchant-icon i { font-size: 22px; }
.merchant-label { font-size: 13px; color: #909399; }
.merchant-value { font-size: 24px; font-weight: bold; color: #303133; margin: 2px 0; }
.merchant-sub { font-size: 12px; color: #c0c4cc; }

.quick-actions {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px,1fr));
  gap: 16px;
}
.quick-item {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 20px 10px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all .2s;
}
.quick-item:hover { background: #ecf5ff; transform: translateY(-2px); }
.quick-item i { font-size: 28px; margin-bottom: 8px; }
.quick-item span { font-size: 13px; color: #606266; }
</style>